import { useSwaggerlessContext } from 'Context/SwaggerlessContext';
import React, { useState, Fragment, useEffect } from 'react';
import { withHousehold } from 'Context/HouseholdContext';
import { householdInvestmentAssetsUri } from 'Consts/advisorUris';
import PropTypes from 'prop-types';
import TotalTable from 'Components/UI/TotalTable';
import Currency from 'Components/Format/Currency';
import AddMoreInfoButton from 'Components/UI/AddMoreInfoButton';
import modalConsts from 'Consts/ModalNames';
import { openModal } from 'Helpers/ModalHelper';
import { sumBalance, fromAPIResponse } from 'Classes/Account';
import './BalanceSheet.css';
import relationships from 'Consts/Relationships';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import { pickComponentAndProps } from 'Helpers/Affordances';


const ManagedAccounts = ({
  managedAccounts,
  managedAccountsLastUpdated,
  aum,
}) => {
  if (managedAccounts.length === 0) {
    return null;
  }

  return (
    <div className="assets-under-management">
      <h4 className="title">Assets Under Management</h4>
      <div className="d-flex align-items-center justify-content-between flex-wrap">
        <span className={aum >= 0 ? 'total-positive' : 'total-negative'}>
          <Currency amount={aum} />
        </span>
        <span
          className="pill"
          onClick={() => openModal(modalConsts.managedAccounts)}
          role="presentation"
        >
          See Accounts
          <span className="circle">{managedAccounts.length}</span>
        </span>
      </div>
      <div className="last-updated">
        Last updated {managedAccountsLastUpdated}
      </div>
    </div>
  );
};

ManagedAccounts.propTypes = {
  managedAccounts: PropTypes.array.isRequired,
  managedAccountsLastUpdated: PropTypes.string.isRequired,
  aum: PropTypes.number.isRequired,
};

const BalanceSheet = ({
  householdId,
  householdResource,
  showTitle,
  data,
}) => {
  const { getAccounts } = useSwaggerlessContext();
  const [liabilities, setLiabilities] = useState([]);
  const [assets, setAssets] = useState([]);
  const [assetTotal, setAssetTotal] = useState(0);
  const [liabilitiesTotal, setLiabilitiesTotal] = useState(0);
  const [hasBalanceSheet, setHasBalanceSheet] = useState(false);

  const [managedAccounts, setManagedAccounts] = useState([]);
  const [aum, setAum] = useState(0);
  const [managedAccountsLastUpdated, setManagedAccountsLastUpdated] = useState('');

  const shapeBalanceSheet = () => {
    setAssetTotal(data.assets.totalBalance);
    setLiabilitiesTotal(data.liabilities.totalBalance);
    setHasBalanceSheet(data.assets.totalBalance !== 0 || data.liabilities.totalBalance !== 0);

    const allAssets = data.assets.items.map((asset) => {
      return ({
        name: asset.name,
        value: asset.sum,
      });
    });
    setAssets(allAssets);

    const allLiabilities = data.liabilities.items.map((liability) => {
      return ({
        name: liability.name,
        value: liability.sum,
      });
    });
    setLiabilities(allLiabilities);
  };

  const fetchManagedAccounts = () => {
    getAccounts(householdId, {}, { isManaged: true })
      .then((res) => {
        const accounts = res.data.map(acct => fromAPIResponse(acct));
        setManagedAccounts(accounts);
        setAum(sumBalance(accounts));
        if (accounts.length > 0 && accounts[0].lastSync.isValid()) {
          const lastSync = accounts[0].lastSync.format('M/D/YY');
          setManagedAccountsLastUpdated(lastSync);
        }
      })
      .catch(err => displayErrorMessage(err, 'Failed to retrieve accounts'));
  };

  useEffect(() => {
    shapeBalanceSheet(data);
  }, [data]);

  useEffect(() => {
    fetchManagedAccounts();
  }, [householdId]);

  const primaryClient = householdResource?.attr('clients').resources().list().find(v => v.attr('isPrimary') === 'true');
  const { component: AssetsUnderManagement, props: assetsUnderManagementProps } = pickComponentAndProps(primaryClient, {
    [relationships.default]: [ManagedAccounts, {
      managedAccounts,
      aum,
      managedAccountsLastUpdated,
    }],
  });

  return (
    <div className="BalanceSheet">
      <div className="landing-balance-sheet">
        {showTitle ? (<h4 className="title">Balance Sheet</h4>) : null}
        <div className="content">
          {hasBalanceSheet ? (
            <Fragment>
              <div className="assets-table">
                <div className="table-header">Assets</div>
                {assetTotal > 0 ? (
                  <TotalTable tableData={assets} />
                ) : (
                  <div className="no-data">No assets.</div>
                )}
              </div>
              <div className="liabilities-table">
                <div className="table-header">Liabilities</div>
                {liabilitiesTotal > 0 ? (
                  <TotalTable
                    tableData={liabilities}
                    rowValueFormat="($0,0)"
                  />
                ) : (
                  <div className="no-data">No liabilities.</div>
                )}
              </div>
            </Fragment>
          ) : (
            <AddMoreInfoButton
              buttonText="Click here to add balance sheet details"
              href={householdInvestmentAssetsUri(householdId)}
            />
          )}
        </div>
      </div>
      <AssetsUnderManagement {...assetsUnderManagementProps} />
    </div>
  );
};

BalanceSheet.defaultProps = {
  showTitle: true,
  isReadOnly: false,
};

BalanceSheet.propTypes = {
  householdId: PropTypes.number.isRequired,
  householdResource: PropTypes.object.isRequired,
  isReadOnly: PropTypes.bool,
  showTitle: PropTypes.bool,
  data: PropTypes.object.isRequired,
};

const BalanceSheetContainer = withHousehold(BalanceSheet);

export default BalanceSheetContainer;
